import Button from 'components/base/Button';
import React, { useEffect, useRef, useState } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { resetPassword, validateResetToken } from 'Api/userapi';
import {  toast } from 'react-hot-toast';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import axios, { AxiosError } from 'axios';
import { ResetPasswordValidationSchema } from 'utils/validationSchemas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faEyeSlash, faEye } from '@fortawesome/free-solid-svg-icons';
interface DecodedToken {
  email: string;
}
const ResetPasswordForm = () => {
  const { token } = useParams();
  const [email, setEmail] = useState('');
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setconfirmPasswordVisible] = useState(false);
  const navigate = useNavigate();
  const isTokenProcessed = useRef(false);

  useEffect(() => {
    if (token && !isTokenProcessed.current) {
      isTokenProcessed.current = true;
      const processToken = async () => {
        try {
          const decodedToken = jwtDecode<DecodedToken>(token);
          if (decodedToken.email) {
            setEmail(decodedToken.email);
            await validateToken(decodedToken.email);
          } else {
            console.error('Email not found in the token');
            setIsTokenValid(false);
          }
        } catch (error) {
          console.error('Token processing failed:', error);
          setIsTokenValid(false);
        }
      };

      processToken();
    }
  }, [token]);

  const validateToken = async (email: string) => {
    if (!token) {
      console.error('Token is undefined');
      setIsTokenValid(false);
      return;
    }

    try {
      const response = await validateResetToken(token);
      if (response.status === 200) {
        setIsTokenValid(true);
      } else {
        const message =
          response.data?.message || 'An unexpected error occurred';
        toast.error(message);
      }
    } catch (error) {
      setIsTokenValid(false);
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message ||
          error.message ||
          'An unexpected error occurred'
        : error instanceof Error
        ? error.message
        : 'An unexpected error occurred';
      toast.error(message);
    }
  };

  const handleSubmit = async (
    values: { password: string; confirmPassword: string },
    { resetForm }: { resetForm: () => void }
  ) => {
    if (!isTokenValid) {
      toast.error('Token is no longer valid. Please request a new one.');
      return;
    }

    try {
      const response = await resetPassword(token!, values);
      if (response.data.status || response.status === 200) {
        resetForm();
        toast.success(`${response.data.message}`);

        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (error: unknown) {
      console.error('Error during password reset:', error);

      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.message || 'An unknown error occurred';
        toast.error(errorMessage);
      } else {
        if (!(error instanceof AxiosError)) {
          toast.error('An unexpected error occurred.');
        }
      }
    }
  };

  return (
    <>
      <div className="text-center mb-6">
        <h4 className="text-body-highlight">Reset password</h4>
        <p className="text-body-tertiary">Enter a new password for {email}</p>

        <Formik
          initialValues={{ password: '', confirmPassword: '' }}
          onSubmit={handleSubmit}
          validationSchema={ResetPasswordValidationSchema}
        >
          {({ isSubmitting, touched, errors }) => (
            <FormikForm className="mt-5">
              <Form.Group className="mb-3 text-start">
                <div className="fs-9">Password</div>
                <InputGroup>
                  <Field
                    name="password"
                    type={passwordVisible ? 'text' : 'password'}
                    className={`form-control form-icon-input ${
                      touched.password && errors.password ? 'is-invalid' : ''
                    }`}
                    placeholder="Password"
                  />
                  <InputGroup.Text
                    className="bg-transparent border-0 cursor-pointer"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                  >
                    <FontAwesomeIcon
                      icon={passwordVisible ? faEye : faEyeSlash}
                      className="fs-9"
                    />
                  </InputGroup.Text>
                </InputGroup>
                <ErrorMessage
                  name="password"
                  component="div"
                  className="text-danger mt-1 fs-9"
                />
              </Form.Group>

              {/* Confirm Password Field */}
              <Form.Group className="mb-3 text-start">
                <div className="fs-9">Confirm Password</div>
                <InputGroup>
                  <Field
                    name="confirmPassword"
                    type={confirmPasswordVisible ? 'text' : 'password'}
                    className={`form-control form-icon-input ${
                      touched.confirmPassword && errors.confirmPassword
                        ? 'is-invalid'
                        : ''
                    }`}
                    placeholder="Confirm Password"
                  />
                  <InputGroup.Text
                    className="bg-transparent border-0 cursor-pointer"
                    onClick={() =>
                      setconfirmPasswordVisible(!confirmPasswordVisible)
                    }
                  >
                    <FontAwesomeIcon
                      icon={confirmPasswordVisible ? faEye : faEyeSlash}
                      className="fs-9"
                    />
                  </InputGroup.Text>
                </InputGroup>
                <ErrorMessage
                  name="confirmPasswordVisible"
                  component="div"
                  className="text-danger mt-1 fs-9"
                />
              </Form.Group>

              {/* Remembered Password Link */}
              <div className="text-start mt-2 text-body-tertiary mb-3">
                Remembered your password?{' '}
                <Link
                  to="/sign-in"
                  className="text-primary text-decoration-none"
                >
                  Sign In
                </Link>
              </div>

              {/* Reset Password Button */}
              <div className="text-center">
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            </FormikForm>
          )}
        </Formik>

      </div>
    </>
  );
};

export default ResetPasswordForm;
